import React, { useState, useEffect } from "react";
import { X, Phone, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";

const ExitIntentPopup = () => {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (dismissed) return;

    const handleMouseLeave = (e) => {
      if (e.clientY <= 5 && !dismissed) {
        setShow(true);
      }
    };

    // Only trigger on desktop
    const isMobile = window.innerWidth < 768;
    if (!isMobile) {
      document.addEventListener("mouseleave", handleMouseLeave);
    }

    // Mobile: trigger after 30s of inactivity
    let mobileTimer;
    if (isMobile) {
      mobileTimer = setTimeout(() => {
        if (!dismissed) setShow(true);
      }, 30000);
    }

    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      clearTimeout(mobileTimer);
    };
  }, [dismissed]);

  const handleDismiss = () => {
    setShow(false);
    setDismissed(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please enter your name and phone");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contacts`, {
        name: form.name,
        phone: form.phone,
        email: "",
        message: "Exit intent popup - needs callback"
      });
      toast.success("We'll call you shortly!");
      handleDismiss();
    } catch {
      toast.error("Call us at 863-340-9244");
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="exit-intent-popup">
      <div className="bg-[#15161C] border border-[#2A2B32] rounded-2xl w-full max-w-md p-6 relative animate-fade-in">
        <button onClick={handleDismiss} className="absolute top-4 right-4 p-1 text-[#A0AAB2] hover:text-white" data-testid="exit-popup-close">
          <X size={20} />
        </button>

        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-white mb-2">Need help with your project?</h3>
          <p className="text-[#A0AAB2] text-sm">Leave your number and we'll call you back in minutes. Free estimates, no pressure.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35]"
            placeholder="Your Name"
            data-testid="exit-form-name"
          />
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
            className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35]"
            placeholder="Phone Number"
            data-testid="exit-form-phone"
          />
          <button
            type="submit"
            disabled={submitting}
            className="w-full gradient-btn py-3.5 rounded-xl font-bold text-base disabled:opacity-50"
            data-testid="exit-form-submit"
          >
            {submitting ? "Sending..." : "Call Me Back"}
          </button>
        </form>

        <div className="text-center mt-4">
          <a href="tel:863-340-9244" className="inline-flex items-center space-x-1.5 text-[#FF6B35] text-sm font-medium hover:underline">
            <Phone size={14} />
            <span>Or call us: 863-340-9244</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
